import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  Renderer2,
  OnDestroy,
  OnInit
} from '@angular/core';

@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnDestroy, OnInit {

  @Input('appTooltip') tooltipTitle: string = ''; // ORA accetta HTML
  @Input() tooltipWidth: string = null;
  @Input() placement: string = 'top';
  @Input() delay: number = 300;
  @Input() appTooltipEnabled: boolean = false;
  @Input() alignText: 'left' | 'right' | 'center' = 'center';
  tooltip!: HTMLElement;
  offset = 10;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  /* ---------------- EVENTI ---------------- */

  @HostListener('mouseenter')
  @HostListener('focus')
  onMouseEnter() {
    if (!this.tooltip) {
      this.show();

      const ariaLabelValue = this.el.nativeElement.getAttribute('aria-label');
      if (!ariaLabelValue || ariaLabelValue !== this.getPlainText()) {
        this.setAriaDescribedBy();
      }
    }
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  onMouseLeave() {
    if (this.tooltip) {
      this.hide();
      this.removeAriaDescribedBy();
    }
  }

  /* ---------------- INIT ---------------- */

  ngOnInit() {
    if (this.appTooltipEnabled) {
      this.checkAndUpdateAriaLabel();
    }
  }

  checkAndUpdateAriaLabel() {
    const hostElement = this.el.nativeElement;
    if (
      hostElement.tagName.toLowerCase() === 'a' &&
      !hostElement.hasAttribute('aria-label') &&
      (hostElement.text.length === 0 || hostElement.text.toLowerCase() === 'apri')
    ) {
      this.renderer.setAttribute(hostElement, 'aria-label', this.getPlainText());
    }
  }

  /* ---------------- TOOLTIP ---------------- */

  show() {
    this.create();
    this.setPosition();
    this.renderer.addClass(this.tooltip, 'ng-tooltip-show');
  }

  hide() {
    if (this.tooltip) {
      this.renderer.removeClass(this.tooltip, 'ng-tooltip-show');

      window.setTimeout(() => {
        if (this.tooltip?.parentNode) {
          this.renderer.removeChild(document.body, this.tooltip);
          this.tooltip = null!;
        }
      }, this.delay);
    }
  }

  create() {
    const tooltipId = 'tooltip-' + Math.random().toString(36).substring(2);

    this.tooltip = this.renderer.createElement('span');

    this.renderer.setAttribute(this.tooltip, 'id', tooltipId);
    this.renderer.setAttribute(this.tooltip, 'role', 'tooltip');
    this.renderer.setAttribute(this.tooltip, 'aria-live', 'polite');
    this.renderer.setAttribute(this.tooltip, 'aria-atomic', 'true');
    this.renderer.setStyle(this.tooltip, 'max-width', this.tooltipWidth);

    /* ✅ CONTENUTO HTML */
    this.tooltip.innerHTML = this.tooltipTitle;

    /* ✅ aria-label SOLO TESTO */
    this.renderer.setAttribute(
      this.tooltip,
      'aria-label',
      this.getPlainText()
    );

    this.renderer.appendChild(document.body, this.tooltip);

    this.renderer.addClass(this.tooltip, 'ng-tooltip');

    this.renderer.setStyle(this.tooltip, 'transition', `opacity ${this.delay}ms`);

    this.renderer.setStyle(this.tooltip, 'text-align', this.alignText);
  }

  /* ---------------- POSIZIONE ---------------- */

  setPosition() {
    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.tooltip.getBoundingClientRect();
    const scrollPos = window.pageYOffset || document.documentElement.scrollTop || 0;

    let top = 0;
    let left = 0;
    let newPlacement = this.placement;

    if (this.placement === 'top') {
      top = hostPos.top - tooltipPos.height - this.offset;
      left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
    }

    if (this.placement === 'bottom') {
      top = hostPos.bottom + this.offset;
      left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
    }

    if (this.placement === 'left') {
      top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
      left = hostPos.left - tooltipPos.width - this.offset;
    }

    if (this.placement === 'right') {
      top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
      left = hostPos.right + this.offset;
    }

    if (left < 0) {
      newPlacement = 'right';
      left = hostPos.right + this.offset;
    } else if (left + tooltipPos.width > window.innerWidth) {
      newPlacement = 'left';
      left = hostPos.left - tooltipPos.width - this.offset;
    }

    this.renderer.setStyle(this.tooltip, 'top', `${top + scrollPos}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
    this.renderer.addClass(this.tooltip, `ng-tooltip-${newPlacement}`);
  }

  /* ---------------- ARIA ---------------- */

  setAriaDescribedBy() {
    this.renderer.setAttribute(
      this.el.nativeElement,
      'aria-describedby',
      this.tooltip.getAttribute('id')!
    );
  }

  removeAriaDescribedBy() {
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-describedby');
  }

  /* ---------------- CLEANUP ---------------- */

  ngOnDestroy() {
    this.removeTooltip();
  }

  removeTooltip() {
    if (this.tooltip?.parentNode) {
      this.renderer.removeChild(document.body, this.tooltip);
      this.tooltip = null!;
    }
  }

  /* ---------------- UTILS ---------------- */

  private getPlainText(): string {
    const div = document.createElement('div');
    div.innerHTML = this.tooltipTitle;
    return div.textContent?.trim() || '';
  }
}
